import { Card, CardBody, Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';

export default function Testimonials() {
  const { t } = useTranslation();
  const testimonials = t('home.testimonials.items', { returnObjects: true }) as Array<{
    initials: string;
    name: string;
    role: string;
    quote: string;
  }>;

  return (
    <section className="relative overflow-hidden bg-[#0D0D0D] py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-6%] top-16 h-80 w-80 rounded-full bg-brand-green/12 blur-3xl" />
        <div className="absolute right-[-10%] top-20 h-[26rem] w-[26rem] rounded-full bg-brand-ocean/12 blur-3xl" />
        <div className="absolute bottom-[-14%] left-[22%] h-72 w-72 rounded-full bg-white/6 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="mb-14 max-w-3xl lg:mb-16">
          <Chip
            variant="flat"
            className="mb-5 border border-white/10 bg-white/6 px-4 py-2 text-sm font-semibold text-brand-green"
            startContent={
              <span className="mr-2 inline-flex items-center">
                <i className="ri-chat-quote-line text-base" />
              </span>
            }
          >
            {t('home.testimonials.badge')}
          </Chip>
          <h2 className="mb-6 text-5xl font-caprasimo text-white md:text-6xl">
            {t('home.testimonials.title')}
          </h2>
          <p className="max-w-2xl text-lg leading-relaxed text-white/72 md:text-xl">
            {t('home.testimonials.description')}
          </p>
        </div>
      </div>

      <div className="relative left-1/2 w-screen -translate-x-1/2">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-12 bg-gradient-to-r from-[#0D0D0D] to-transparent md:w-24" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-12 bg-gradient-to-l from-[#0D0D0D] to-transparent md:w-24" />

        <div className="overflow-hidden px-6 pb-4 pt-3 md:px-8 xl:px-10">
          <div className="testimonials-marquee-track">
            {[0, 1].map((copyIndex) => (
              <div
                key={copyIndex}
                className="flex shrink-0 gap-6 pr-6 md:gap-7 md:pr-7"
                aria-hidden={copyIndex === 1}
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={`${copyIndex}-${testimonial.name}`}
                    className="w-[88vw] shrink-0 md:w-[36rem] xl:w-[41rem]"
                  >
                    <Card className="h-full rounded-[32px] border border-white/10 bg-[rgba(26,26,26,0.92)] backdrop-blur-sm shadow-[0_14px_34px_rgba(0,0,0,0.24)] transition-all duration-300 hover:border-brand-green/24 hover:shadow-[0_18px_42px_rgba(0,0,0,0.3)]">
                      <CardBody className="flex h-full flex-col gap-8 p-8 md:p-10">
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex items-center gap-4">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-green/15 text-lg font-bold text-brand-green shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                              {testimonial.initials}
                            </div>
                            <div>
                              <div className="text-lg font-semibold text-white">
                                {testimonial.name}
                              </div>
                              {testimonial.role ? (
                                <div className="mt-1 text-sm leading-relaxed text-white/58">
                                  {testimonial.role}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="text-5xl font-semibold leading-none text-brand-green/28">
                            “
                          </div>
                        </div>

                        <p className="max-w-3xl text-lg leading-8 text-white/80 md:text-[1.08rem]">
                          {testimonial.quote}
                        </p>

                          <div className="mt-auto flex items-center justify-between gap-4 border-t border-white/10 pt-5">
                          <div className="text-sm font-medium text-white/48">
                            {t('home.testimonials.referenceLabel')} {String(index + 1).padStart(2, '0')}
                          </div>
                          <div className="text-sm font-medium text-brand-green/70">
                            {t('home.testimonials.verified')}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
